import { useMemo, useState } from "react";
import { api, formatApiError, openSecureFile } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { Sparkles, X, Trophy, AlertTriangle, TrendingUp, CheckCircle2, Loader2, Download, Paperclip } from "lucide-react";

/**
 * Modale di analisi AI dei preventivi ricevuti (stato "Inviato").
 * Raggruppa i preventivi confrontabili (per ticket_id o descrizione) e permette
 * di lanciare uno scoring tramite Jev che valuta coerenza, completezza, chiarezza
 * economica ed anomalie. Il risultato è ADVISORY (nessuna decisione automatica).
 */
export function AnalisiAIPreventiviModal({ open, onClose, preventivi }) {
  const [analisi, setAnalisi] = useState(null);
  const [loading, setLoading] = useState(false);
  const [gruppoAttivoKey, setGruppoAttivoKey] = useState(null);

  // Raggruppa i preventivi con offerta ricevuta (stati con offerta) per oggetto AI, ticket_id o descrizione normalizzata.
  // Iter123k200 · "oggetto_ai" (compilato dall'admin in creazione) ha priorità assoluta:
  // è il modo esplicito per legare richieste identiche inviate a più fornitori.
  // Non richiediamo il campo importo compilato (spesso vuoto: PDF firmato via "Conferma preventivo").
  const STATI_CON_OFFERTA = ["Inviato", "IncaricoAccettato", "Accettato", "Ricevuto", "Sottoscritto"];
  const normDesc = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim().slice(0, 120);
  const gruppi = useMemo(() => {
    const map = new Map();
    (preventivi || []).forEach((p) => {
      if (!STATI_CON_OFFERTA.includes(p.stato)) return;
      const oggetto = normDesc(p.oggetto_ai);
      const key = oggetto ? `ogg::${oggetto}` : (p.ticket_id || `desc::${normDesc(p.descrizione)}`);
      if (!key || key === "desc::" || key === "ogg::") return;
      if (!map.has(key)) {
        map.set(key, {
          key,
          etichetta: (p.oggetto_ai || "").trim()
            ? (p.oggetto_ai || "").slice(0, 90)
            : (p.ticket_numero ? `Manutenzione n. ${p.ticket_numero}` : (p.descrizione || "").slice(0, 90)),
          oggetto_ai: (p.oggetto_ai || "").trim(),
          descrizione: p.descrizione || "",
          condominio_nome: p.condominio_nome || "",
          items: [],
        });
      }
      map.get(key).items.push(p);
    });
    return Array.from(map.values()).filter((g) => g.items.length >= 2);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preventivi]);

  const analizza = async (gruppo) => {
    setLoading(true);
    setGruppoAttivoKey(gruppo.key);
    setAnalisi(null);
    try {
      const r = await api.post("/preventivi/analisi-ai", {
        preventivo_ids: gruppo.items.map((p) => p.id),
      });
      setAnalisi({ gruppo, data: r.data });
    } catch (e) {
      toast.error(formatApiError(e) || "Analisi AI non disponibile. Riprovare tra qualche istante.");
      setGruppoAttivoKey(null);
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-3" data-testid="analisi-ai-modal">
      <Card className="w-full max-w-4xl max-h-[92vh] overflow-y-auto border-2 border-brand/40">
        <div className="sticky top-0 bg-white border-b-2 border-brand/20 px-4 py-3 flex items-center justify-between z-10">
          <h3 className="font-bold text-lg text-slate-800 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-brand" /> Analisi AI dei preventivi
          </h3>
          <Button data-testid="analisi-ai-close" variant="ghost" size="icon" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        <div className="p-4 space-y-4">
          <p className="text-sm text-slate-600 bg-brand/5 border border-brand/20 rounded-lg p-3">
            <strong className="text-brand">Come funziona:</strong> l'AI confronta le offerte ricevute per la stessa richiesta e produce
            uno scoring su <strong>coerenza, completezza, chiarezza economica</strong> ed
            eventuali anomalie. <strong>La decisione finale spetta sempre a te.</strong>
          </p>

          {!analisi && gruppi.length === 0 && (
            <div className="text-center py-8 text-slate-500">
              Nessun gruppo di offerte confrontabili al momento.<br />
              Servono almeno <strong>2 preventivi con offerta ricevuta</strong> per la stessa richiesta.
            </div>
          )}

          {!analisi && gruppi.length > 0 && (
            <>
              <div className="text-sm font-semibold text-slate-700">
                {gruppi.length} {gruppi.length === 1 ? "gruppo confrontabile" : "gruppi confrontabili"} disponibili:
              </div>
              <div className="space-y-2">
                {gruppi.map((g) => (
                  <div key={g.key} data-testid={`gruppo-confrontabile-${g.key}`}
                    className="border-2 border-slate-200 rounded-lg p-3 hover:border-brand/40 transition-colors">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        {g.oggetto_ai && (
                          <div className="inline-flex items-center gap-1 mb-1 px-2 py-0.5 rounded-full bg-[#FBEEF3] text-brand text-[11px] font-bold uppercase tracking-wide">
                            <Sparkles className="w-3 h-3" /> Oggetto AI
                          </div>
                        )}
                        <div className="font-semibold text-slate-800 truncate">{g.etichetta}</div>
                        {g.condominio_nome && <div className="text-xs text-slate-500">{g.condominio_nome}</div>}
                        <div className="text-xs text-slate-600 mt-1 font-semibold">
                          {g.items.length} offerte dei fornitori:
                        </div>
                        <ul className="mt-1 space-y-1" data-testid={`gruppo-offerte-${g.key}`}>
                          {g.items.map((p) => {
                            const allegati = (p.allegati && p.allegati.length) ? p.allegati.length : (p.allegato ? 1 : 0);
                            return (
                              <li key={p.id} data-testid={`gruppo-offerta-${p.id}`}
                                className="flex items-center justify-between gap-2 text-xs bg-slate-50 border border-slate-200 rounded px-2 py-1.5">
                                <div className="min-w-0">
                                  <span className="font-semibold text-slate-800 truncate">{p.fornitore_nome}</span>
                                  {p.importo ? <span className="text-slate-500"> · {p.importo}</span> : null}
                                  {allegati > 0 ? (
                                    <span className="ml-1 text-slate-500">· <Paperclip className="inline w-3 h-3" /> {allegati} allegat{allegati === 1 ? "o" : "i"}</span>
                                  ) : (
                                    <span className="ml-1 text-slate-400 italic">· nessun allegato</span>
                                  )}
                                </div>
                                {allegati > 0 && (
                                  <Button type="button" size="sm" variant="outline"
                                    data-testid={`scarica-allegati-offerta-${p.id}`}
                                    className="h-7 px-2 text-[11px] border-brand/40 text-brand hover:bg-[#FBEEF3]"
                                    onClick={async () => {
                                      try { await openSecureFile(`/preventivi/${p.id}/allegati-offerta.zip`); }
                                      catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Download non riuscito"); }
                                    }}>
                                    <Download className="w-3 h-3 mr-1" /> Allegati
                                  </Button>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                      <Button
                        data-testid={`analizza-gruppo-${g.key}`}
                        onClick={() => analizza(g)}
                        disabled={loading}
                        className="shrink-0 bg-brand hover:bg-brand/90 text-white"
                        size="sm"
                      >
                        {loading && gruppoAttivoKey === g.key ? (
                          <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Analisi…</>
                        ) : (
                          <><Sparkles className="w-4 h-4 mr-1" /> Analizza</>
                        )}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {analisi && (
            <RisultatiAnalisi data={analisi.data} gruppo={analisi.gruppo} onBack={() => { setAnalisi(null); setGruppoAttivoKey(null); }} />
          )}
        </div>
      </Card>
    </div>
  );
}

function RisultatiAnalisi({ data, gruppo, onBack }) {
  const medalIcon = (pos) => pos === 1 ? "🥇" : pos === 2 ? "🥈" : pos === 3 ? "🥉" : `#${pos}`;
  const allegatiCountByPid = useMemo(() => {
    const m = {};
    (gruppo?.items || []).forEach((p) => {
      m[p.id] = (p.allegati && p.allegati.length) ? p.allegati.length : (p.allegato ? 1 : 0);
    });
    return m;
  }, [gruppo]);
  const coerColor = (label) => {
    if (label === "alta") return "bg-emerald-100 text-emerald-800 border-emerald-300";
    if (label === "media") return "bg-amber-100 text-amber-800 border-amber-300";
    return "bg-red-100 text-red-800 border-red-300";
  };
  const scoreBar = (val, max) => {
    const pct = Math.round((val / max) * 100);
    return (
      <div className="w-full bg-slate-100 rounded-full h-2">
        <div className="bg-brand h-2 rounded-full transition-all" style={{ width: `${pct}%` }} />
      </div>
    );
  };

  return (
    <div className="space-y-4" data-testid="analisi-ai-risultati">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold text-slate-700">Risultato analisi</div>
          <div className="text-xs text-slate-500 truncate max-w-xl">{gruppo.etichetta}</div>
        </div>
        <Button variant="outline" size="sm" onClick={onBack} data-testid="analisi-ai-back">← Altri gruppi</Button>
      </div>

      {data.results.some((r) => r.convenienza_pct === null) && (
        <div className="text-xs bg-slate-50 border border-slate-200 rounded-lg p-2 text-slate-600" data-testid="analisi-no-importi">
          <strong>Nota:</strong> per uno o più preventivi l'importo non è registrato nel sistema (offerta acquisita via PDF firmato).
          Il confronto di <strong>convenienza economica</strong> è quindi omesso; i pesi sono ridistribuiti su coerenza, completezza e chiarezza.
        </div>
      )}

      <div className="space-y-3">
        {data.results.map((r) => (
          <div key={r.preventivo_id} data-testid={`analisi-risultato-${r.preventivo_id}`}
            className={`border-2 rounded-lg p-3 ${r.posizione === 1 ? "border-emerald-300 bg-emerald-50/30" : "border-slate-200"}`}>
            <div className="flex items-center justify-between gap-2 mb-2">
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-2xl">{medalIcon(r.posizione)}</span>
                <div className="min-w-0">
                  <div className="font-bold text-slate-800 truncate">{r.fornitore_nome}</div>
                  <div className="text-xs text-slate-500">n. {r.numero} · {r.importo}</div>
                </div>
              </div>
              <div className="shrink-0 text-right">
                <div className="text-2xl font-bold text-brand">{r.score_finale}</div>
                <div className="text-xs text-slate-500">Score composito</div>
                {allegatiCountByPid[r.preventivo_id] > 0 && (
                  <Button type="button" size="sm" variant="outline"
                    data-testid={`scarica-allegati-risultato-${r.preventivo_id}`}
                    className="mt-1 h-7 px-2 text-[11px] border-brand/40 text-brand hover:bg-[#FBEEF3]"
                    onClick={async () => {
                      try { await openSecureFile(`/preventivi/${r.preventivo_id}/allegati-offerta.zip`); }
                      catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Download non riuscito"); }
                    }}>
                    <Download className="w-3 h-3 mr-1" /> Allegati
                  </Button>
                )}
              </div>
            </div>

            {r.anomalie.flag && (
              <div className="mb-2 flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">
                <AlertTriangle className="w-4 h-4 shrink-0" />
                Rilevate possibili anomalie ({Math.round(r.anomalie.probabilita_si * 100)}%). Rivedi con attenzione.
              </div>
            )}

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
              <div>
                <div className="font-semibold text-slate-600 mb-1">Coerenza</div>
                <span className={`inline-block px-2 py-0.5 rounded-full border font-bold uppercase ${coerColor(r.coerenza.label)}`}>
                  {r.coerenza.label}
                </span>
              </div>
              <div>
                <div className="font-semibold text-slate-600 mb-1">Completezza</div>
                <div className="text-slate-800 font-medium">{r.completezza.label}</div>
                {scoreBar(r.completezza.score, 4)}
              </div>
              <div>
                <div className="font-semibold text-slate-600 mb-1">Chiarezza economica</div>
                <div className="text-slate-800 font-medium">{r.chiarezza_economica.label}</div>
                {scoreBar(r.chiarezza_economica.score, 3)}
              </div>
              <div>
                <div className="font-semibold text-slate-600 mb-1 flex items-center gap-1">
                  <TrendingUp className="w-3 h-3" /> Convenienza
                </div>
                <div className="text-slate-800 font-medium">
                  {r.convenienza_pct != null ? `${r.convenienza_pct}%` : "—"}
                </div>
                {r.convenienza_pct != null && scoreBar(r.convenienza_pct, 100)}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 flex items-start gap-2">
        <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
        <div>{data.disclaimer}</div>
      </div>
    </div>
  );
}
